import React, { useState, useEffect, useRef } from "react";
import { auth, db, rtdb } from "../../firebase";
import { ref as rtdbRef, set } from "firebase/database";
import EmojiPicker from 'emoji-picker-react'; // 👈 Emoji Picker
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  getDocFromServer,
  updateDoc,
  increment,
  setDoc

} from "firebase/firestore";
import { encryptMessage, encryptHybrid, importKey, generateFileKey, encryptFile, exportKey } from "../../utils/crypto";
import { SenderKeyManager } from "../../utils/SenderKeyManager";
import { useTheme } from "../../context/ThemeContext";

// ⚡ OPTIMIZED: Added selectedUser & currentUserData props
export default function MessageInput({ convId, editingMessage, setEditingMessage, isSquad, selectedUser, currentUserData }) {
  const [text, setText] = useState("");

  // ✏️ Sync Edit State
  useEffect(() => {
    if (editingMessage) {
      // If it was encrypted, we might need the decrypted 'text' which ChatWindow might have put in.
      // ChatWindow passes 'm'. 'm.text' should be decrypted there.
      setText(editingMessage.text || "");
      if (fileInputRef.current) fileInputRef.current.focus();
    } else {
      setText("");
    }
  }, [editingMessage]);
  const [username, setUsername] = useState("");
  const [listening, setListening] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const typingTimeout = useRef(null);
  const { isDarkMode } = useTheme();

  // ☁️ Cloudinary & Preview State
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mediaType, setMediaType] = useState("image"); // 'image' or 'video'
  const fileInputRef = useRef(null);

  // ✅ Define recipientUid robustly
  const recipientUid = convId ? convId.split('_').find(id => id !== auth.currentUser?.uid) : null;

  // 🚀 Key Cache to prevent repetitive imports (Slow WebCrypto)
  const pubKeyCache = useRef({});

  const cachedImportKey = async (jwkStr) => {
    if (!jwkStr) return null;
    if (pubKeyCache.current[jwkStr]) return pubKeyCache.current[jwkStr];

    try {
      const key = await importKey(jwkStr, "encrypt");
      if (key) pubKeyCache.current[jwkStr] = key;
      return key;
    } catch (e) { console.error("Key import error", e); return null; }
  };

  useEffect(() => {
    const fetchUsername = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        setUsername(userDoc.data().username || "Unknown");
      }
    };
    fetchUsername();
  }, []);

  const sendMediaMessage = async (url, type, captionText = "") => {
    try {
      let finalMediaUrl = url;
      let senderMediaUrl = url;
      let isEncrypted = false;
      let finalText = captionText;
      let senderText = captionText;
      const myUid = auth.currentUser.uid;


      if (recipientUid && !isSquad) {
        // ⚡ OPTIMIZED: Use Props instead of Fetching
        // We already have selectedUser (Recipient) and currentUserData (Me) from ChatUI

        const encryptForDevices = async (devicesList, plainData) => {
          const payloads = {};
          let successCount = 0;
          for (const device of devicesList || []) {
            if (!device.publicKey) continue;
            try {
              const pubKey = await importKey(device.publicKey, "encrypt");
              if (pubKey) {
                // 💎 USE HYBRID ENCRYPTION FOR ALL MESSAGES
                const cipher = await encryptHybrid(plainData, pubKey);
                payloads[device.deviceId] = cipher;
                successCount++;
              }
            } catch (e) { console.warn("Enc failed", e); }
          }
          return successCount > 0 ? JSON.stringify({ v: 2, payloads }) : null;
        };

        // --- Encrypt Media URL ---
        // 1. Recipient (selectedUser)
        if (selectedUser) {
          if (selectedUser.devices?.length > 0) {
            const res = await encryptForDevices(selectedUser.devices, url);
            if (res) { finalMediaUrl = res; isEncrypted = true; }
          } else if (selectedUser.publicKey) {
            // Legacy V1 (Now Upgraded to Hybrid)
            const legacyKey = await importKey(selectedUser.publicKey, "encrypt");
            if (legacyKey) { finalMediaUrl = await encryptHybrid(url, legacyKey); isEncrypted = true; }
          }
        }

        // 2. Sender (currentUserData)
        if (currentUserData) {
          const localPubKey = localStorage.getItem(`pubKey_${auth.currentUser?.uid}`);
          if (currentUserData.devices?.length > 0) {
            const res = await encryptForDevices(currentUserData.devices, url);
            if (res) senderMediaUrl = res;
          } else if (localPubKey) {
            const mk = await importKey(localPubKey, "encrypt");
            if (mk) senderMediaUrl = await encryptHybrid(url, mk);
          } else if (currentUserData.publicKey) {
            const mk = await importKey(currentUserData.publicKey, "encrypt");
            if (mk) senderMediaUrl = await encryptHybrid(url, mk);
          }
        }

        // --- Encrypt Caption (if present) ---
        if (captionText) {
          if (isEncrypted) { // Only encrypt text if media was encrypted (implies user has keys)
            // Recipient
            if (selectedUser?.devices?.length > 0) {
              const txtRes = await encryptForDevices(selectedUser.devices, captionText);
              if (txtRes) finalText = txtRes;
            } else if (selectedUser?.publicKey) {
              const lk = await importKey(selectedUser.publicKey, "encrypt");
              if (lk) finalText = await encryptHybrid(captionText, lk);
            }

            // Sender
            const localPubKey = localStorage.getItem(`pubKey_${auth.currentUser?.uid}`);
            if (currentUserData?.devices?.length > 0) {
              const myTxtRes = await encryptForDevices(currentUserData.devices, captionText);
              if (myTxtRes) senderText = myTxtRes;
            } else if (localPubKey) {
              const mk = await importKey(localPubKey, "encrypt");
              if (mk) senderText = await encryptHybrid(captionText, mk);
            } else if (currentUserData?.publicKey) {
              const mk = await importKey(currentUserData.publicKey, "encrypt");
              if (mk) senderText = await encryptHybrid(captionText, mk);
            }
          }
        }
      }

      await addDoc(collection(db, "messages"), {
        convId,
        text: finalText,
        senderText: senderText,
        mediaUrl: finalMediaUrl,
        senderMediaUrl: senderMediaUrl,
        mediaType: type,
        senderId: auth.currentUser.uid,
        senderUsername: username,
        isEncrypted: isEncrypted,
        timestamp: serverTimestamp(),
      });

      // Update conversation
      const lastMsgPreview = isEncrypted
        ? (type === 'image' ? '📷 Encrypted Photo' : '🎥 Encrypted Video')
        : (captionText
          ? (type === 'image' ? '📷 ' + captionText : '🎥 ' + captionText)
          : (type === 'image' ? '📷 Image' : '🎥 Video'));

      await setDoc(doc(db, "conversations", convId), {
        updatedAt: serverTimestamp(),
        lastMessage: lastMsgPreview,
        lastMessageTimestamp: serverTimestamp(),
        lastMessageSenderId: auth.currentUser.uid,
        isLastMessageEncrypted: isEncrypted,
        unreadCounts: { [recipientUid]: increment(1) } // ✅ Fixed undefined ref
      }, { merge: true });

    } catch (error) {
      console.error("Error sending media:", error);
      alert("Failed to send media.");
    }
  };

  // ... (handleFileSelect and other logic remains)
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 🚫 Block Video Files
    if (file.type.startsWith("video/")) {
      alert("Video uploads are disabled. You can only send images and documents.");
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // Limit to 100MB
      alert("File is too large. Max 100MB.");
      return;
    }

    // Auto-detect type
    const type = file.type.startsWith("image/") ? "image" : "file";
    setMediaType(type);
    setPreviewFile(file);

    // Only preview if it's an image
    if (type === 'image') {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl("GENERIC_FILE"); // Marker for UI
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl !== "GENERIC_FILE") URL.revokeObjectURL(previewUrl);
    setPreviewFile(null);
    setPreviewUrl(null);
    setText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadAndSend = async (e) => {
    e.preventDefault();
    if (!previewFile) return;


    setIsUploading(true);
    try {
      let uploadedUrl = null;
      let iv = null;
      let fileKey = null;
      let fileKeyJwk = null;

      if (isSquad) {
        // 📂 SQUAD File Upload (Plain)
        // Simply upload the file directly without encryption
        const uploadFileToCloudinary = async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("upload_preset", "chatiq");
          formData.append("cloud_name", "dofw2fffg");

          const res = await fetch(`https://api.cloudinary.com/v1_1/dofw2fffg/auto/upload`, {
            method: "POST",
            body: formData
          });
          const data = await res.json();
          if (data.error) throw new Error(data.error.message);
          return data.secure_url;
        };
        uploadedUrl = await uploadFileToCloudinary(previewFile);

      } else {
        // 🔒 ENCRYPTED File Upload
        // 1. GENERATE KEY & ENCRYPT FILE
        fileKey = await generateFileKey();
        const { encryptedBlob, iv: generatedIv } = await encryptFile(previewFile, fileKey);
        iv = generatedIv;

        // 2. UPLOAD ENCRYPTED BLOB (Chunked if needed)
        const uploadFileToCloudinary = async (blob) => {
          const CHUNK_SIZE = 6 * 1024 * 1024; // 6MB chunks
          const totalSize = blob.size;

          if (totalSize < CHUNK_SIZE) {
            // Simple Upload
            const formData = new FormData();
            formData.append("file", blob, "encrypted.dat");
            formData.append("upload_preset", "chatiq");
            formData.append("cloud_name", "dofw2fffg");

            const res = await fetch(`https://api.cloudinary.com/v1_1/dofw2fffg/auto/upload`, {
              method: "POST",
              body: formData
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error.message);
            return data.secure_url;
          }

          // Chunked Upload
          const uniqueId = Date.now() + "_" + Math.random().toString(36).substr(2, 9);
          let start = 0;
          let end = 0;
          let secureUrl = null;

          while (start < totalSize) {
            end = Math.min(start + CHUNK_SIZE, totalSize);
            const chunk = blob.slice(start, end);

            const formData = new FormData();
            formData.append("file", chunk, "encrypted.dat");
            formData.append("upload_preset", "chatiq");
            formData.append("cloud_name", "dofw2fffg");

            const range = `bytes ${start}-${end - 1}/${totalSize}`;

            const res = await fetch(`https://api.cloudinary.com/v1_1/dofw2fffg/auto/upload`, {
              method: "POST",
              body: formData,
              headers: {
                "X-Unique-Upload-Id": uniqueId,
                "Content-Range": range
              }
            });

            const data = await res.json();
            if (data.error) throw new Error(data.error.message);

            if (data.secure_url) secureUrl = data.secure_url;

            start = end;
          }
          return secureUrl;
        };

        uploadedUrl = await uploadFileToCloudinary(encryptedBlob);
      }

      if (!uploadedUrl) throw new Error("Upload failed (No URL)");

      // 3. PREPARE KEY PAYLOAD (Encrypt AES Key for Recipient)
      // Only for Encrypted Chats
      let mediaKeyPayload = null;
      let senderMediaKeyPayload = null;

      if (!isSquad) {
        fileKeyJwk = await exportKey(fileKey);

        const encryptKeyForDevices = async (devicesList, plainKey) => {
          const payloads = {};
          for (const device of devicesList || []) {
            try {
              if (!device.publicKey) continue;
              const pubKey = await importKey(device.publicKey, "encrypt");
              const cipher = await encryptMessage(plainKey, pubKey);
              payloads[device.deviceId] = cipher;
            } catch (e) { }
          }
          return payloads;
        };

        // Encrypt for Recipient (from Props)
        mediaKeyPayload = {};
        if (selectedUser?.devices) {
          mediaKeyPayload = await encryptKeyForDevices(selectedUser.devices, fileKeyJwk);
        }

        // Encrypt for Self (from Props)
        senderMediaKeyPayload = {};
        if (currentUserData?.devices) {
          senderMediaKeyPayload = await encryptKeyForDevices(currentUserData.devices, fileKeyJwk);
        }
      }

      // 4. SEND MESSAGE
      await addDoc(collection(db, "messages"), {
        convId,
        text: text,
        mediaUrl: uploadedUrl, // Use the returned URL
        mediaIv: iv, // IV for AES-GCM
        mediaKey: mediaKeyPayload, // Keys for Recipient
        senderMediaKey: senderMediaKeyPayload, // Keys for Sender
        mimeType: previewFile.type,
        fileName: previewFile.name,
        mediaType: mediaType, // 'image', 'video', 'file'
        senderId: auth.currentUser.uid,
        senderUsername: username,
        isEncryptedFile: !isSquad, // Flag for UI
        timestamp: serverTimestamp(),
      });

      // Update Last Message
      const lastMsgLabel = mediaType === 'image' ? '📷 Encrypted Image' : (mediaType === 'video' ? '🎥 Encrypted Video' : '📎 Encrypted File');
      await setDoc(doc(db, "conversations", convId), {
        updatedAt: serverTimestamp(),
        lastMessage: lastMsgLabel,
        lastMessageTimestamp: serverTimestamp(),
        lastMessageSenderId: auth.currentUser.uid,
        isLastMessageEncrypted: true,
        unreadCounts: { [recipientUid]: increment(1) }
      }, { merge: true });

      closePreview();

    } catch (error) {
      console.error("Secure Upload Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const messageToSend = text.trim();
    if (!messageToSend || !convId) return;

    setText("");
    const typingRef = rtdbRef(rtdb, `/typing/${convId}/${auth.currentUser.uid}`);
    set(typingRef, false);

    // ✏️ EDIT MODE HANDLER
    if (editingMessage) {
      try {
        let finalText = messageToSend;
        let senderText = messageToSend;
        let isEncrypted = false;
        const myUid = auth.currentUser.uid;

        // RE-ENCRYPT Logic (Same as send)
        // ⚡ EDIT MODE: Use Props
        if (selectedUser) {
          // ... existing edit encryption logic using selectedUser/currentUserData ...
          // For brevity in this fix, we will just replicate the logic quickly or trust the user is online.
          // Ideally we refactor 'encryptForDevices' to be reusable, but for now I'll inline the prop usage.
          // Since 'Edit' is less critical for speed than 'Send', we can leave it or patch it.
          // Let's patch it to use props too for consistency.

          const encryptForDevices = async (devicesList, plainText) => {
            const payloads = {};
            if (!devicesList) return null;
            let successCount = 0;
            for (const device of devicesList) {
              if (!device.publicKey) continue;
              try {
                const pubKey = await importKey(device.publicKey, "encrypt");
                const cipher = await encryptHybrid(plainText, pubKey);
                if (cipher) { payloads[device.deviceId] = cipher; successCount++; }
              } catch (err) { }
            }
            return successCount > 0 ? JSON.stringify({ v: 2, payloads }) : null;
          };

          const rData = selectedUser;
          if (rData.devices?.length > 0) {
            const res = await encryptForDevices(rData.devices, messageToSend);
            if (res) { finalText = res; isEncrypted = true; }
          } else if (rData.publicKey) {
            const lk = await importKey(rData.publicKey, "encrypt");
            if (lk) { finalText = await encryptHybrid(messageToSend, lk); isEncrypted = true; }
          }

          const mData = currentUserData;
          if (mData) {
            if (mData.devices?.length > 0) {
              const res = await encryptForDevices(mData.devices, messageToSend);
              if (res) senderText = res;
            } else if (mData.publicKey) {
              const mk = await importKey(mData.publicKey, "encrypt");
              if (mk) senderText = await encryptHybrid(messageToSend, mk);
            }
          }
        }

        // UPDATE MESSAGE DOC
        await updateDoc(doc(db, "messages", editingMessage.id), {
          text: finalText,
          senderText: senderText,
          isEncrypted: isEncrypted,
          isEdited: true
        });

        // UPDATE LAST MESSAGE (If this is the last one)
        // We assume backend/integrity checks or just do it optimistically if it matches logic
        // Ideally we check if this ID matches conversation.lastMessageId? We don't track ID there easily.
        // But user requirement is "latest message", so it IS the last message.
        const lastMsgLabel = isEncrypted ? "🔒 Encrypted Message" : finalText;
        await setDoc(doc(db, "conversations", convId), {
          lastMessage: lastMsgLabel,
          isLastMessageEncrypted: isEncrypted
        }, { merge: true });

        setEditingMessage(null);

      } catch (e) {
        console.error("Edit Failed", e);
        alert("Failed to update message.");
        setText(messageToSend); // Restore text on fail
      }
      return; // STOP HERE
    }

    try {
      let finalText = messageToSend;
      let senderText = messageToSend;
      let isEncrypted = false;
      let isSquadEncrypted = false; // Flag for SenderKey payload
      let senderKeyPayload = {};    // { ciphertext, iv, keyId, v:3 }

      const myUid = auth.currentUser.uid;

      if (isSquad) {
        // 🛡️ SQUAD E2EE (Sender Key)
        try {
          // 1. Get Key ID (Cache/Local) or Rotate
          // We need member list for rotation.
          let members = selectedUser?.members || [];
          if (members.length === 0) {
            const sSnap = await getDoc(doc(db, "squads", convId));
            if (sSnap.exists()) members = sSnap.data().members || [];
          }

          // Try to get active key logic... 
          // Ideally we store currentKeyId in LocalStorage or fetch latest.
          // Simple approach: Check if we have a valid key.
          // For now, let's use a "current_key_id_${convId}" marker in localStorage?
          // Or better: SenderKeyManager.getSenderKey needs a specific KeyID.
          // We need to know WHICH key to use. 
          // Strategy: 
          // 1. Check if I have a "my_current_group_key_${convId}" stored locally.
          // 2. If yes, Use it.
          // 3. If no, Rotate (Generate New) and save it.

          let myKeyId = localStorage.getItem(`my_active_key_${convId}`);
          let senderKey = myKeyId ? await SenderKeyManager.getSenderKey(convId, myKeyId) : null;

          if (!senderKey) {
            console.log("Rotating Sender Key...");
            myKeyId = await SenderKeyManager.rotateMySenderKey(convId, members);
            if (myKeyId) {
              localStorage.setItem(`my_active_key_${convId}`, myKeyId);
              senderKey = await SenderKeyManager.getSenderKey(convId, myKeyId);
            }
          }

          if (senderKey && myKeyId) {
            const encResult = await SenderKeyManager.encryptText(messageToSend, senderKey);
            if (encResult) {
              senderKeyPayload = {
                ciphertext: encResult.ciphertext,
                iv: encResult.iv,
                keyId: myKeyId,
                v: 3, // Version 3: Sender Key
                isSquad: true
              };
              finalText = "🔒 Encrypted Message"; // Placeholder for notifications/legacy
              senderText = "🔒 Encrypted Message";
              isEncrypted = true;
              isSquadEncrypted = true;
            }
          }
        } catch (err) {
          console.error("Squad Encryption Failed", err);
          // Fallback to plain text? User pref says "Security First", but we fallback for usability if critical failure?
          // No, let's fail safe or send plain if system is broken?
          // Existing logic sends plain. We'll stick to that if enc fails.
        }
      } else if (recipientUid) {
        // ⚡ OPTIMIZED: Use Props first, fallback to Cache (never Server)

        let recipientData = selectedUser;
        let myData = currentUserData;

        // Fallback: If props are missing usage data (rare case), try cache
        if (!recipientData || !myData) {
          console.warn("Props missing in MessageInput, falling back to cache");
          const [rSnap, mSnap] = await Promise.all([
            getDoc(doc(db, "users", recipientUid)), // CACHE preferred
            getDoc(doc(db, "users", myUid))
          ]);
          if (rSnap.exists()) recipientData = rSnap.data();
          if (mSnap.exists()) myData = mSnap.data();
        }

        const encryptForDevices = async (devicesList, plainText) => {
          const payloads = {};
          let successCount = 0;

          // Helper for legacy single key logic
          if ((!devicesList || devicesList.length === 0) && recipientData?.publicKey) {
            const legacyKey = await cachedImportKey(recipientData.publicKey);
            if (legacyKey) return await encryptHybrid(plainText, legacyKey);
          }

          for (const device of devicesList || []) {
            if (!device.publicKey) continue;
            const pubKey = await cachedImportKey(device.publicKey); // 🚀 Use Cache
            if (pubKey) {
              try {
                const cipher = await encryptHybrid(plainText, pubKey);
                payloads[device.deviceId] = cipher;
                successCount++;
              } catch (e) { }
            }
          }
          return successCount > 0 ? JSON.stringify({ v: 2, payloads }) : null;
        };

        // 💎 Prepare My Key for Self-Encryption (sk) logic kept for fallback
        let myKey = null;
        const localPubKey = localStorage.getItem(`pubKey_${auth.currentUser?.uid}`);
        if (localPubKey) myKey = await cachedImportKey(localPubKey);
        else if (myData?.publicKey) myKey = await cachedImportKey(myData.publicKey);

        if (recipientData) {
          if (recipientData.devices?.length > 0) {
            const res = await encryptForDevices(recipientData.devices, messageToSend);
            if (res) { finalText = res; isEncrypted = true; }
          } else if (recipientData.publicKey) {
            const lk = await cachedImportKey(recipientData.publicKey); // 🚀 Use Cache
            // 💎 FIX: Pass 'myKey' as 3rd arg to include 'sk' in payload
            if (lk) {
              finalText = await encryptHybrid(messageToSend, lk, myKey);
              isEncrypted = true;
            }
          }
        }

        // Sender Encrypt (Robust V2 Injection)
        if (myData) {
          let devicesToUse = myData.devices || [];

          // 💎 FORCE INJECT CURRENT DEVICE KEY
          // This ensures that even if Firestore is stale, we encrypt for our CURRENT Local Key.
          const currentDeviceId = localStorage.getItem("deviceId");
          if (currentDeviceId && localPubKey) {
            // Clone list to avoid mutation issues
            devicesToUse = [...devicesToUse.filter(d => d.deviceId !== currentDeviceId)];
            devicesToUse.push({ deviceId: currentDeviceId, publicKey: localPubKey });
          }

          if (devicesToUse.length > 0) {
            const res = await encryptForDevices(devicesToUse, messageToSend);
            if (res) senderText = res;
          } else {
            // Fallback if no devices and no injection worked (rare)
            if (isEncrypted && finalText && !recipientData?.devices?.length) {
              senderText = finalText;
            } else if (myKey) {
              senderText = await encryptHybrid(messageToSend, myKey);
            }
          }
        }
      }

      await addDoc(collection(db, "messages"), {
        convId,
        text: finalText,
        senderText: senderText,
        senderId: auth.currentUser.uid,
        senderUsername: username,
        isEncrypted: isEncrypted,
        ...senderKeyPayload, // Inject ciphertext, keyId, iv, v
        timestamp: serverTimestamp(),
      });

      await setDoc(doc(db, "conversations", convId), {
        updatedAt: serverTimestamp(),
        lastMessage: isEncrypted ? "🔒 Encrypted Message" : finalText,
        lastMessageTimestamp: serverTimestamp(),
        lastMessageSenderId: auth.currentUser.uid,
        isLastMessageEncrypted: isEncrypted,
        unreadCounts: { [recipientUid]: increment(1) } // ✅ Fixed
      }, { merge: true });

    } catch (err) {
      console.error("Send failed:", err);
      setText(messageToSend);
    }



    // 🔔 Notification logic removed (cleaned up) as requested.
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      previewUrl ? handleUploadAndSend(e) : handleSend(e);
    }
  };

  const handleVoiceInput = () => {
    if (!("webkitSpeechRecognition" in window)) { alert("Not supported"); return; }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onresult = (e) => setText((p) => p + " " + e.results[0][0].transcript);
    recognition.start();
  };

  const handleTyping = () => {
    if (!convId) return;
    const r = rtdbRef(rtdb, `/typing/${convId}/${auth.currentUser.uid}`);
    set(r, true);
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => set(r, false), 1500);
  };

  const onEmojiClick = (obj) => { setText(p => p + obj.emoji); handleTyping(); };

  return (
    <div className="relative z-20 flex flex-col">
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar" />

      {/* 🟢 FULLSCREEN PREVIEW OVERLAY */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-[#0b141a] flex flex-col animate-fade-in">
          {/* Top Bar */}
          <div className="flex items-center justify-between p-4 bg-black/20 backdrop-blur-sm z-10">
            <button onClick={closePreview} className="text-white hover:bg-white/10 p-2 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <h2 className="text-white font-medium text-lg">Preview {mediaType === 'video' ? 'Video' : 'Photo'}</h2>
            <div className="w-10"></div>{/* Spacer */}
          </div>

          {/* Media Center */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden bg-black/50">
            {previewUrl === "GENERIC_FILE" ? (
              <div className="text-white text-center">
                <div className="text-6xl mb-4">📎</div>
                <p className="font-mono">{previewFile?.name}</p>
                <p className="text-sm text-gray-400">{(previewFile?.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (mediaType === 'image' ? (
              <img src={previewUrl} alt="Preview" className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" />
            ) : (
              <video src={previewUrl} controls className="max-h-full max-w-full rounded-lg shadow-2xl" />
            ))}
          </div>

          {/* Caption & Send Bar */}
          <div className="p-4 bg-black/40 backdrop-blur-md pb-8">
            <form onSubmit={handleUploadAndSend} className="max-w-3xl mx-auto flex items-end gap-3">
              <div className="flex-1 bg-white/10 hover:bg-white/20 focus-within:bg-white/20 rounded-3xl flex items-center px-5 py-3 border border-white/10 transition-all">
                <textarea
                  rows={1}
                  className="w-full bg-transparent outline-none border-none resize-none text-white placeholder-gray-400 max-h-32 custom-scrollbar text-lg"
                  placeholder="Add a caption..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={isUploading}
                className="p-4 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 🟢 Normal Input Bar (Hidden if Preview Active? No, kept in background or visible) */}
      {!previewUrl && (
        <>
          {showEmoji && (
            <div className="w-full bg-gray-50/90 backdrop-blur-sm border-t border-gray-200 animate-slide-up origin-bottom flex flex-col relative z-30">
              <div className="flex justify-end px-4 py-2 bg-white/50 border-b border-gray-100">
                <button type="button" onClick={() => setShowEmoji(false)} className="p-1 rounded-full hover:bg-gray-200 text-gray-500"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>
              </div>
              <div className="flex justify-center p-2">
                <EmojiPicker theme={isDarkMode ? 'dark' : 'light'} onEmojiClick={onEmojiClick} lazyLoad={true} width="100%" height={300} searchDisabled={false} skinTonesDisabled={true} previewConfig={{ showPreview: false }} />
              </div>
            </div>
          )}

          <form
            onSubmit={handleSend}
            className="flex items-end gap-2 px-4 py-3 bg-gray-100 border-t border-gray-200 dark:bg-white/10 dark:backdrop-blur-md dark:border-white/20 select-none relative z-20 transition-colors"
          >
            {/* TOGGLE ONE-TIME MODE BUTTON */}


            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className={`p-3 rounded-full transition-colors flex items-center justify-center shrink-0 ${isUploading ? 'animate-pulse text-indigo-500' : 'text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-white/10'}`}
              title="Attach Image or Video"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
            </button>

            <div className="flex-1 rounded-3xl flex items-center px-4 py-2 shadow-sm border-2 transition-all bg-white border-indigo-500 dark:bg-white/10 dark:border-white/10 focus-within:ring-2 focus-within:ring-indigo-500/50 dark:focus-within:ring-indigo-500/50">
              {/* 🛑 Force Black Text in Light Mode via Style Tag */}
              {!isDarkMode && (
                <style>
                  {`
                    #chat-message-input {
                      color: #000000 !important;
                      -webkit-text-fill-color: #000000 !important;
                      caret-color: #000000 !important;
                    }
                    #chat-message-input::placeholder {
                      color: #4b5563 !important; /* Gray-600 */
                      -webkit-text-fill-color: #4b5563 !important;
                    }
                  `}
                </style>
              )}
              <textarea
                id="chat-message-input"
                rows={1}
                className="w-full bg-transparent outline-none border-none resize-none text-black dark:text-white dark:placeholder:text-gray-400 max-h-32 custom-scrollbar leading-relaxed font-medium"
                placeholder={convId ? "Type a message..." : "Select a conversation..."}
                value={text}
                onChange={(e) => { setText(e.target.value); handleTyping(); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                onKeyDown={handleKeyDown}
                disabled={!convId}
                autoComplete="off"
              />
              <button type="button" onClick={() => setShowEmoji(!showEmoji)} className={`p-2 rounded-full transition-colors ml-2 ${showEmoji ? 'text-indigo-500 bg-indigo-50' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
              </button>
            </div>

            <div className="pb-0.5">
              {text.trim() ? (
                <button type="submit" className="p-3 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg transition-transform active:scale-95 flex items-center justify-center">
                  {editingMessage ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                  )}
                </button>
              ) : (
                editingMessage ? (
                  <button type="button" onClick={() => setEditingMessage(null)} className="p-3 rounded-full bg-gray-400 hover:bg-gray-500 text-white flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                ) : (
                  <button type="button" onClick={handleVoiceInput} className={`p-3 rounded-full shadow-md transition-all flex items-center justify-center ${listening ? "bg-red-500 text-white animate-pulse" : "bg-[#00a884] text-white hover:bg-[#008f6f] dark:bg-[#00a884]/20 dark:text-[#00a884] dark:border dark:border-[#00a884]/30"}`}>
                    {listening ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="6" height="6" rx="1" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>}
                  </button>
                ))}
            </div>
          </form>
        </>
      )}
    </div>
  );
}
